import React, { useContext, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { AppToasterV2, MultiStepItemProps } from "@ucl/library";
import variables from "@config/variables";
import "./AccountSignupStep.scss";

export interface AccountSignupStepProps extends MultiStepItemProps {
  financialApplicationId: string;
  returnToUrl?: string;
}

export const AccountSignupStep: React.FC<AccountSignupStepProps> = (props) => {
  const { loginWithRedirect, isAuthenticated, user, logout } = useAuth0();
  const { clientType } = useContext(UserContext);

  // Default to consumer if no client type is set
  const currentClientType = clientType || "consumer";

  // Register submit function with the multi-step container
  useEffect(() => {
    const submitFunction = async () => {
      if (!isAuthenticated) {
        // throw an app toaster
        AppToasterV2.showFailedActionToaster("You must be signed in to continue.");
      }

      return isAuthenticated;
    };

    props.registerSubmit?.(submitFunction);
  }, [isAuthenticated, props.registerSubmit]);

  const getAuthConfig = () => {
    if (currentClientType === "consumer") {
      return {
        orgId: variables.consumerAuth0OrgId,
        logoutUri: variables.consumerAuth0LogoutUri,
      };
    } else {
      return {
        orgId: variables.bankerAuth0OrgId,
        logoutUri: variables.bankerAuth0LogoutUri,
      };
    }
  };

  const authConfig = getAuthConfig();

  const handleSignup = () => {
    loginWithRedirect({
      appState: {
        returnTo: props.returnToUrl || window.location.href,
      },
      authorizationParams: {
        screen_hint: "signup",
        organization: authConfig.orgId,
      },
    });
  };

  const handleLogin = () => {
    loginWithRedirect({
      appState: {
        returnTo: props.returnToUrl || window.location.href,
      },
      authorizationParams: {
        organization: authConfig.orgId,
      },
    });
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: authConfig.logoutUri,
      },
    });
  };

  if (isAuthenticated && user) {
    return (
      <section className="account-signup step-section">
        <div className="account-signup__header step-header">Welcome!</div>
        <div className="account-signup__subtitle step-subtitle">
          You're successfully signed in. Let's continue with your application.
        </div>
        <div className="account-signup__content step-content">
          <div className="account-signup__user-info">
            <div className="account-signup__user-details">
              <p>
                <strong>Email:</strong> {user.email}
              </p>
              {user.email_verified && <p className="account-signup__verified">✓ Email verified</p>}
            </div>
            <button
              className="account-signup__button account-signup__button--secondary"
              onClick={handleLogout}
            >
              Sign Out
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="account-signup step-section">
      <div className="account-signup__header step-header">Create Account or Sign In</div>
      <div className="account-signup__subtitle step-subtitle">
        To continue with your financial application, you'll need to create an account or sign in.
      </div>
      <div className="account-signup__content step-content">
        <div className="account-signup__actions">
          <button
            className="account-signup__button account-signup__button--primary"
            onClick={handleSignup}
          >
            Create New Account
          </button>

          <div className="account-signup__divider">
            <span>or</span>
          </div>

          <button
            className="account-signup__button account-signup__button--secondary"
            onClick={handleLogin}
          >
            Sign Into Existing Account
          </button>
        </div>

        {/* <div className="account-signup__info">
          <p>
            <strong>Why create an account?</strong>
          </p>
          <ul>
            <li>Save your progress and return later</li>
            <li>Track your application status</li>
            <li>Receive important updates about your application</li>
            <li>Access your account information securely</li>
          </ul>
        </div> */}
      </div>
    </section>
  );
};
